import { RoutePaths } from 'core/models';
import { MenuItem, NavBottomListItem, NotificationBell } from 'hfs-common-ui';
import NavMenuItem from '../NavMenuItem';
import * as S from './styled';

const getUptimeMenu = (isDesktop?: boolean, isDealersReportEnabled?: boolean) => {
  const uptimeMenuMap = new Map<RoutePaths, NavBottomListItem>([
    [
      RoutePaths.Dashboard,
      {
        id: 1,
        label: <NavMenuItem id="dashboard_menu" labelKey="CORE:menu.dashboard" path={RoutePaths.Dashboard} />,
        link: RoutePaths.Dashboard,
        icon: { type: 'action.statistics' },
      },
    ],
    [
      RoutePaths.Assets,
      {
        id: 2,
        label: <NavMenuItem id="asset_menu" labelKey="CORE:menu.assets" path={RoutePaths.Assets} />,
        link: RoutePaths.Assets,
        icon: { type: 'product.automower_top' },
      },
    ],
    [
      RoutePaths.Companies,
      {
        id: 3,
        label: <NavMenuItem id="company_menu" labelKey="CORE:menu.companies" path={RoutePaths.Companies} />,
        link: RoutePaths.Companies,
        icon: { type: 'action.group' },
      },
    ],
    [
      RoutePaths.AssetsMap,
      {
        id: 4,
        label: <NavMenuItem id="map_menu" labelKey="CORE:menu.map" path={RoutePaths.AssetsMap} />,
        link: RoutePaths.AssetsMap,
        icon: { type: 'action.map' },
      },
    ],
  ]);

  uptimeMenuMap.set(RoutePaths.Maintenance, {
    id: 5,
    label: <NavMenuItem id="maintenance_menu" labelKey="CORE:menu.maintenance" path={RoutePaths.Maintenance} />,
    link: RoutePaths.Maintenance,
    icon: { type: 'action.maintenance' },
  });
  if (isDealersReportEnabled) {
    uptimeMenuMap.set(RoutePaths.Dealers, {
      id: 6,
      label: <NavMenuItem id="dealers_menu" labelKey="CORE:menu.dealers" path={RoutePaths.Dealers} />,
      link: RoutePaths.Dealers,
      icon: { type: 'action.context_menu_vertical' },
    });
  }
  if (!isDesktop) {
    uptimeMenuMap.set(RoutePaths.More, {
      id: 7,
      label: <NavMenuItem id="settings_menu" labelKey="CORE:menu.more" path={RoutePaths.More} />,
      link: RoutePaths.More,
      icon: { type: 'action.context_menu_vertical' },
    });
  }

  return { menuMap: uptimeMenuMap, menu: Array.from(uptimeMenuMap.values()).sort((a, b) => a.id - b.id) };
};

const getAssetTabMenu = (hasContract?: boolean) => {
  const assetTabMenuMap = new Map<RoutePaths, MenuItem>([
    [
      RoutePaths.AssetDetailProductInfomation,
      {
        id: 1,
        label: (
          <NavMenuItem
            id="product_information_menu"
            labelKey="ASSETS:details.labels.productInfo"
            path={RoutePaths.AssetDetailProductInfomation}
          />
        ),
        link: RoutePaths.AssetDetailProductInfomation,
      },
    ],
    [
      RoutePaths.AssetDetailContacts,
      {
        id: 2,
        label: (
          <NavMenuItem
            id="conatact_menu"
            labelKey="ASSETS:details.labels.contact"
            path={RoutePaths.AssetDetailContacts}
          />
        ),
        link: RoutePaths.AssetDetailContacts,
      },
    ],
  ]);

  if (hasContract) {
    assetTabMenuMap.set(RoutePaths.AssetDetailServiceContract, {
      id: 3,
      label: (
        <NavMenuItem
          id="service_contract_menu"
          labelKey="ASSETS:details.labels.services"
          path={RoutePaths.AssetDetailServiceContract}
        />
      ),
      link: RoutePaths.AssetDetailServiceContract,
    });
  }

  return { menuMap: assetTabMenuMap, menu: Array.from(assetTabMenuMap.values()) };
};

const getDropdownMenu = (isDesktop: boolean, unreadChangelogEntries: number, feedbackUrl: string) => {
  const assetDropdownMenuMap = new Map<RoutePaths, MenuItem>();
  if (isDesktop) {
    assetDropdownMenuMap.set(RoutePaths.WhatIsNew, {
      id: 1,
      label: (
        <S.WhatIsNewDiv>
          <NotificationBell size="medium" notificationCount={unreadChangelogEntries} />
          <NavMenuItem id="what-is-new-menu" labelKey="CORE:menu.dropdown.whatIsNew" path={RoutePaths.WhatIsNew} />
        </S.WhatIsNewDiv>
      ),
      link: RoutePaths.WhatIsNew,
    });
    assetDropdownMenuMap.set(RoutePaths.Faq, {
      id: 2,
      label: <NavMenuItem id="faq_menu" labelKey="CORE:menu.dropdown.faq" path={RoutePaths.Faq} />,
      iconLeft: 'action.customer_support',
      link: RoutePaths.Faq,
    });
  }

  if (!isDesktop) {
    assetDropdownMenuMap.set(RoutePaths.UserInfo, {
      id: 1,
      label: <NavMenuItem id="settings_menu" labelKey="CORE:menu.dropdown.user" path={RoutePaths.UserInfo} />,
      iconLeft: 'action.avatar',
      link: RoutePaths.UserInfo,
    });

    assetDropdownMenuMap.set(RoutePaths.WhatIsNew, {
      id: 2,
      label: (
        <S.WhatIsNewDiv>
          <NotificationBell size="medium" notificationCount={unreadChangelogEntries} />
          <NavMenuItem id="what-is-new-menu" labelKey="CORE:menu.dropdown.whatIsNew" path={RoutePaths.WhatIsNew} />
        </S.WhatIsNewDiv>
      ),
      link: RoutePaths.WhatIsNew,
    });

    assetDropdownMenuMap.set(RoutePaths.Faq, {
      id: 3,
      label: <NavMenuItem id="faq_menu" labelKey="CORE:menu.dropdown.faq" path={RoutePaths.Faq} />,
      iconLeft: 'action.customer_support',
      link: RoutePaths.Faq,
    });
  }

  assetDropdownMenuMap.set(RoutePaths.ThirdPartySoftware, {
    id: 4,
    label: (
      <NavMenuItem
        id="third-party-software-menu"
        labelKey="CORE:menu.dropdown.thirdPartySoftware"
        path={RoutePaths.ThirdPartySoftware}
      />
    ),
    iconLeft: 'product.operators_manual',
    link: RoutePaths.ThirdPartySoftware,
  });

  assetDropdownMenuMap.set(RoutePaths.CookiePolicy, {
    id: 5,
    label: <NavMenuItem id="cookie-policy-menu" labelKey="CORE:menu.dropdown.cookiePolicy" path="#" />,
    iconLeft: 'other.cookie',
    iconRight: 'action.external_link',
    link: RoutePaths.CookiePolicy,
  });
  if (feedbackUrl && !isDesktop) {
    assetDropdownMenuMap.set(RoutePaths.GiveFeedback, {
      id: 6,
      label: <NavMenuItem id="feedback-menu" labelKey="CORE:menu.dropdown.feedback" path={RoutePaths.GiveFeedback} />,
      iconLeft: 'action.bubble',
      iconRight: 'action.external_link',
      link: feedbackUrl,
    });
  }

  return { menuMap: assetDropdownMenuMap, menu: Array.from(assetDropdownMenuMap.values()) };
};

export { getUptimeMenu, getAssetTabMenu, getDropdownMenu };
